import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';
import type { User } from '../../types';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface UserFormProps {
  mode: 'create' | 'edit';
}

const UserForm: React.FC<UserFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    is_staff: false,
    is_superuser: false,
    is_active: true,
  });

  useEffect(() => {
    if (mode === 'edit' && id) {
      loadUser();
    }
  }, [mode, id]);

  const loadUser = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const userData = await authApi.getUser(parseInt(id));
      setFormData({
        username: userData.username,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        password: '',
        confirm_password: '',
        is_staff: userData.is_staff,
        is_superuser: userData.is_superuser || false,
        is_active: userData.is_active,
      });
    } catch (err: any) {
      setError('Erro ao carregar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validations
    if (!formData.username || !formData.email || !formData.first_name || !formData.last_name) {
      setError('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (mode === 'create' && (!formData.password || !formData.confirm_password)) {
      setError('Por favor, preencha a senha e confirmação');
      return;
    }

    // Validação de senha para mudança de senha em modo edit
    if (mode === 'edit' && formData.password) {
      if (formData.password !== formData.confirm_password) {
        setError('Nova senha e confirmação não coincidem');
        return;
      }
      if (formData.password.length < 6) {
        setError('A nova senha deve ter pelo menos 6 caracteres');
        return;
      }
    }

    // Validação de senha para modo create
    if (formData.password && formData.password !== formData.confirm_password) {
      setError('Senha e confirmação não coincidem');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (mode === 'create') {
        await authApi.createUser({
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          password: formData.password,
          is_staff: formData.is_staff,
          is_superuser: formData.is_superuser,
          is_active: formData.is_active,
        });
      } else if (id) {
        const updateData: any = {
          username: formData.username,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          is_staff: formData.is_staff,
          is_superuser: formData.is_superuser,
          is_active: formData.is_active,
        };

        // Se nova senha foi fornecida, incluir no update
        if (formData.password) {
          updateData.password = formData.password;
        }

        await authApi.updateUser(parseInt(id), updateData);
      }

      navigate('/users');
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        `Erro ao ${mode === 'create' ? 'criar' : 'atualizar'} usuário`
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentUser?.is_staff) {
    return (
    <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Acesso negado. Apenas administradores podem acessar esta página.
        </div>
      </div>
    );
  }

  if (isLoading && mode === 'edit') {
    return (
    <div className="space-y-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-secondary-600">Carregando usuário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-base font-bold text-secondary-900">
          {mode === 'create' ? 'Criar Novo Usuário' : 'Editar Usuário'}
        </h1>
        <p className="text-secondary-600">
          {mode === 'create' 
            ? 'Preencha os dados para criar um novo usuário no sistema'
            : 'Edite os dados do usuário'
          }
        </p>
      </div>

      <div className="bg-white shadow-sm rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-secondary-700 mb-2">
                Nome de Usuário *
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="input-field"
                placeholder="Digite o nome de usuário"
                value={formData.username}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                Email *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="input-field"
                placeholder="Digite o email"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-secondary-700 mb-2">
                Nome *
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                required
                className="input-field"
                placeholder="Digite o nome"
                value={formData.first_name}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-secondary-700 mb-2">
                Sobrenome *
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                required
                className="input-field"
                placeholder="Digite o sobrenome"
                value={formData.last_name}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Password Section */}
          {mode === 'create' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                  Senha *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-field pr-10"
                    placeholder="Digite a senha"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-secondary-400" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-secondary-400" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-secondary-500">Mínimo de 6 caracteres</p>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-secondary-700 mb-2">
                  Confirmar Senha *
                </label>
                <input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  required
                  className="input-field"
                  placeholder="Digite novamente a senha"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Change Password Section for Edit Mode */}
          {mode === 'edit' && (
            <div className="space-y-6">
              <div className="border-t border-secondary-200 pt-6">
                <h3 className="text-lg font-medium text-secondary-900 mb-4">
                  Alterar Senha (Opcional)
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
                      Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        className="input-field pr-10"
                        placeholder="Digite a nova senha"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-4 w-4 text-secondary-400" />
                        ) : (
                          <EyeIcon className="h-4 w-4 text-secondary-400" />
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-secondary-500">
                      Deixe vazio para manter a senha atual. Minimo 6 caracteres.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="confirm_password" className="block text-sm font-medium text-secondary-700 mb-2">
                      Confirmar Nova Senha
                    </label>
                    <input
                      id="confirm_password"
                      name="confirm_password"
                      type="password"
                      className="input-field"
                      placeholder="Digite novamente a nova senha"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Permissions */}
          <div className="border-t border-secondary-200 pt-6">
            <h3 className="text-lg font-medium text-secondary-900 mb-4">Permissões</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="is_active"
                  name="is_active"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-secondary-900">
                  Usuário ativo
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="is_staff"
                  name="is_staff"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  checked={formData.is_staff}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <label htmlFor="is_staff" className="ml-2 block text-sm text-secondary-900">
                  Administrador (acesso a funções administrativas)
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="is_superuser"
                  name="is_superuser"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  checked={formData.is_superuser}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <label htmlFor="is_superuser" className="ml-2 block text-sm text-secondary-900">
                  Super Administrador (acesso total ao sistema)
                </label>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-2 pt-6 border-t border-secondary-200">
            <button
              type="button"
              onClick={() => navigate('/users')}
              className="px-3 py-1.5 border border-secondary-300 text-secondary-700 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {mode === 'create' ? 'Criando...' : 'Salvando...'}
                </div>
              ) : (
                mode === 'create' ? 'Criar Usuário' : 'Salvar Alterações'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;